import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  FormDateField,
  FormSection,
  FormSelectField,
  FormStatusField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { FULL_ROW_FIELD_CLASS } from '@/components/common/FormDialog/formDialog.constants';
import { GeoUnitSelect } from '@/components/common/GeoUnitSelect';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { activeExamBoardsQueryOptions } from '@/features/examBoards/api/examBoards.api';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { instituteFormSchema, type InstituteFormValues } from '../types/institute.types';

interface InstituteFormProps {
  defaultValues: InstituteFormValues;
  initialDivisionId?: number | undefined;
  initialDistrictId?: number | undefined;
  onSubmit: (values: InstituteFormValues) => void;
  onCancel?: () => void;
  isPending: boolean;
  submitLabel: string;
  pendingLabel: string;
}

export function InstituteForm({
  defaultValues,
  initialDivisionId,
  initialDistrictId,
  onSubmit,
  onCancel,
  isPending,
  submitLabel,
  pendingLabel,
}: InstituteFormProps) {
  const form = useForm<InstituteFormValues>({
    resolver: zodResolver(instituteFormSchema),
    defaultValues,
  });

  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(form.formState.isDirty);

  function handleCancel() {
    if (!onCancel) return;
    askBeforeClose(onCancel);
  }

  const { data: instituteTypes, isLoading: typesLoading } = useQuery(
    activeInstituteTypesQueryOptions(),
  );
  const { data: examBoards, isLoading: examBoardsLoading } = useQuery(
    activeExamBoardsQueryOptions(),
  );

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormSection>
            <FormTextField
              control={form.control}
              name="name"
              label="Name"
              placeholder="Dhaka Model School"
              isRequired
            />
            <FormTextField
              control={form.control}
              name="nameBn"
              label="Name (Bangla)"
              placeholder="ঢাকা মডেল স্কুল"
            />
            <FormTextareaField
              control={form.control}
              name="description"
              label="Description"
              placeholder="A short description of this institute"
              isFullRow
            />
          </FormSection>

          <FormSection>
            <FormSelectField
              control={form.control}
              name="instituteTypeId"
              label="Institute Type"
              options={instituteTypes}
              isLoading={typesLoading}
              placeholder="Select institute type"
              isRequired
            />
            <FormSelectField
              control={form.control}
              name="examBoardId"
              label="Exam Board"
              options={examBoards}
              isLoading={examBoardsLoading}
              placeholder="Select exam board"
              isRequired
            />
            <FormField
              control={form.control}
              name="geoUnitId"
              render={({ field, fieldState }) => (
                <FormItem className={FULL_ROW_FIELD_CLASS}>
                  <FormControl>
                    <GeoUnitSelect
                      value={field.value}
                      onChange={field.onChange}
                      divisionId={initialDivisionId}
                      districtId={initialDistrictId}
                      required
                      error={fieldState.error?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection>
            <FormTextField
              control={form.control}
              name="contactName"
              label="Contact Name"
              placeholder="Karim Uddin"
              isRequired
            />
            <FormTextField
              control={form.control}
              name="contactNumber"
              label="Contact Number"
              placeholder="01711223344"
              inputMode="numeric"
              hasTabularNumbers
              isRequired
            />
            <FormTextField
              control={form.control}
              name="eiinNumber"
              label="EIIN Number"
              placeholder="123456"
              inputMode="numeric"
              hasTabularNumbers
            />
          </FormSection>

          <FormSection>
            <FormDateField control={form.control} name="startDate" label="Start Date" isRequired />
            <FormDateField control={form.control} name="endDate" label="End Date" isRequired />
          </FormSection>

          <FormStatusField control={form.control} name="isActive" />

          <div className="flex items-center justify-end gap-3">
            {onCancel ? (
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                Cancel
              </Button>
            ) : null}
            <Button type="submit" disabled={isPending} className="gap-1.5">
              {isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {pendingLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </Form>
      {discardChangesDialog}
    </>
  );
}
